import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { UserRole } from '@/types/roles';
import { APP_SUBDOMAIN, getSubdomain } from '@/utils/subdomainUtils';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import './RoleGuard.css';

interface RoleGuardProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  allowedRoles,
  children,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <ProgressSpinner aria-label={t('common.loading')} />
      </div>
    );
  }

  // Not authenticated - redirect to appropriate login
  if (!isAuthenticated || !user) {
    const returnUrl = location.pathname + location.search;
    const subdomain = getSubdomain();

    // On app subdomain, redirect to provider selection page
    if (subdomain === APP_SUBDOMAIN) {
      return (
        <Navigate
          to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
          replace
        />
      );
    }

    // On organization subdomains, redirect directly to Cognito
    const cognitoUrl = `/api/auth/cognito/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = cognitoUrl;
    return (
      <div className='flex justify-center items-center h-screen'>
        <ProgressSpinner aria-label={t('common.loading')} />
      </div>
    );
  }

  // Check if the user has any of the allowed roles in ANY of their JWT org
  // memberships. Previously this checked only `organizations[0]`, which
  // silently denied access for cross-tree grants (e.g. SuperAdmin granted at
  // an org other than the user's home organization). B2C users have no organizations[] and won't pass.
  const userRoles = user.organizations?.flatMap(org => org.roles) ?? [];
  const hasAllowedRole = allowedRoles.some(allowedRole =>
    userRoles.includes(allowedRole)
  );

  if (!hasAllowedRole) {
    return <Navigate to='/home' replace />;
  }

  return <>{children}</>;
};
